// ProductList.tsx
import React, { useState, useEffect } from 'react';
import { Search, Plus, Eye, Edit, Trash2, Star, CheckCircle, XCircle } from 'lucide-react'; // Icons
import { Link } from 'react-router-dom';
import axios from 'axios'; // Import axios for API calls

// Unified Product type for display
interface Product {
  id: number; // Using srno from backend as id
  itemId?: number; // Optional, might not be displayed but useful for fetching
  image: string; // imageUrl from backend
  name: string; // itemName from backend
  price: number; // price from backend
  stock: number; // stockQuantity from backend
  category: string; // category from backend
  // Add other fields you might need for edit/view, but not necessarily for the list view

  productType: 'gold' | 'silver';
}

interface Notification {
  message: string;
  type: 'success' | 'error';
}

const ProductList: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [productTypeFilter, setProductTypeFilter] = useState('All'); // 'All', 'gold', 'silver'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // --- NEW STATES FOR CUSTOM CONFIRMATION/ALERT ---
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [notification, setNotification] = useState<Notification | null>(null);

  useEffect(() => {
    fetchProducts();
  }, [productTypeFilter]); // Refetch when productTypeFilter changes

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      let goldProducts: Product[] = [];
      let silverProducts: Product[] = [];

     if (productTypeFilter === 'All' || productTypeFilter === 'gold') {
  // const goldResponse = await axios.get<any[]>('http://localhost:8080/api/admin/gold-products');
  const goldResponse = await axios.get<any[]>('https://sardar-jewellers.com/api/admin/gold-products');
  goldProducts = goldResponse.data.map(goldItem => ({
  id: goldItem.srno,
  itemId: goldItem.itemId,
  image: goldItem.images && goldItem.images.length > 0
  ? goldItem.images[0].imagePath // ✅ Already contains full http://localhost:8080 path
  : 'https://dummyimage.com/150x150/cccccc/000000&text=No+Image', // alternate if needed
  name: goldItem.itemName,
  price: goldItem.price,
  stock: goldItem.stockQuantity,
  category: goldItem.category,
  productType: 'gold',
}));
}

 if (productTypeFilter === 'All' || productTypeFilter === 'silver') {
  // const silverResponse = await axios.get<any[]>('http://localhost:8080/api/admin/silver-products');
  const silverResponse = await axios.get<any[]>('https://sardar-jewellers.com/api/admin/silver-products');
  silverProducts = silverResponse.data.map(silverItem => ({
  id: silverItem.srno,
  itemId: silverItem.itemId,
  image: silverItem.images && silverItem.images.length > 0
  ? silverItem.images[0].imagePath // ✅ Already contains full http://localhost:8080 path
  : 'https://dummyimage.com/150x150/cccccc/000000&text=No+Image', // alternate if needed
  name: silverItem.itemName,
  price: silverItem.price,
  stock: silverItem.stockQuantity,
  category: silverItem.category,
  productType: 'silver',
}));

      // if (productTypeFilter === 'All' || productTypeFilter === 'silver') {
      //   const silverResponse = await axios.get<any[]>('http://localhost:8080/api/admin/silver-products');
      //   silverProducts = silverResponse.data.map(silverItem => ({
      //     id: silverItem.srno,
      //     itemId: silverItem.itemId,
      //     image: silverItem.imageUrl || 'https://via.placeholder.com/150?text=No+Image', // Placeholder if no image
      //     name: silverItem.itemName,
      //     price: silverItem.price,
      //     stock: silverItem.stockQuantity,
      //     category: silverItem.category,
      //   }));
      }

      setProducts([...goldProducts, ...silverProducts]);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Failed to fetch products. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteConfirm = (product: Product) => {
    setProductToDelete(product);
    setIsConfirmOpen(true);
    setNotification(null); // Clear any previous notification
  };
  
  // --- NEW: Function to handle the actual deletion (from modal) ---
  const handleConfirmedDelete = async () => {
    if (!productToDelete) return;

    const { id, productType, name } = productToDelete;
    setIsConfirmOpen(false); // Close the modal

    try {
      // await axios.delete(`http://localhost:8080/api/admin/${productType}-products/${id}`);
      await axios.delete(`https://sardar-jewellers.com/api/admin/${productType}-products/${id}`);
      
      // Show success notification
      setNotification({
        message: `${name} (${productType.toUpperCase()}) successfully deleted!`,
        type: 'success',
      });
      fetchProducts(); // Refresh the list
    } catch (error) {
      console.error(`Error deleting ${productType} product:`, error);
      
      // Show error notification
      setNotification({
        message: `Failed to delete ${name} (${productType.toUpperCase()}). Please check the server logs.`,
        type: 'error',
      });
    } finally {
      setProductToDelete(null); // Clear the product
      // Auto-hide the notification after 5 seconds
      setTimeout(() => setNotification(null), 5000);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleCategoryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCategory(event.target.value);
  };

  // const handleDeleteProduct = async (id: number, type: 'gold' | 'silver') => {
  //   if (window.confirm(`Are you sure you want to delete this ${type} product?`)) {
  //     try {
  //       await axios.delete(`http://localhost:8080/api/admin/${type}-products/${id}`);
  //       alert(`${type} Product deleted successfully!`);
  //       fetchProducts(); // Refresh the list
  //     } catch (error) {
  //       console.error(`Error deleting ${type} product:`, error);
  //       alert(`Failed to delete ${type} product.`);
  //     }
  //   }
  // };

  // ProductList.tsx
const handleDeleteProduct = async (id: number, type: 'gold' | 'silver') => { // Use the passed type
    if (window.confirm(`Are you sure you want to delete this ${type} product?`)) {
        try {
            // The `type` variable is now guaranteed to be 'gold' or 'silver'
            // await axios.delete(`http://localhost:8080/api/admin/${type}-products/${id}`);
            await axios.delete(`https://sardar-jewellers.com/api/admin/${type}-products/${id}`);
            alert(`${type} Product deleted successfully!`);
            fetchProducts(); // Refresh the list
        } catch (error) {
            console.error(`Error deleting ${type} product:`, error);
            alert(`Failed to delete ${type} product.`);
        }
    }
};

  // const filteredProducts = products.filter((product) => {
  //   const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
  //   const matchesCategory =
  //     selectedCategory === 'All' || product.category.toLowerCase() === selectedCategory.toLowerCase();
  //   return matchesSearch && matchesCategory;
  // });

  // // Extract unique categories for the filter dropdown
  // // const uniqueCategories = ['All', ...new Set(products.map(p => p.category))];

  // if (loading) {
  //   return <div className="text-center py-8">Loading products...</div>;
  // }

  // if (error) {
  //   return <div className="text-center py-8 text-red-500">{error}</div>;
  // }
const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === 'All' || product.category.toLowerCase() === selectedCategory.toLowerCase();
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return <div className="text-center py-8">Loading products...</div>;
  }

  if (error) {
    return <div className="text-center py-8 text-red-500">{error}</div>;
  }
  
  // --- NEW: Confirmation Modal Component ---
  const DeleteConfirmationModal: React.FC = () => {
    if (!isConfirmOpen || !productToDelete) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-600 bg-opacity-50 flex justify-center items-center">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md m-4 p-6">
          <h3 className="text-lg font-bold text-red-600 mb-4">Confirm Deletion</h3>
          <p className="text-gray-700 mb-6">
            Are you sure you want to permanently delete the product: 
            <span className="font-semibold text-gray-900 ml-1">{productToDelete.name}</span> 
            (ID: {productToDelete.id})?
          </p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setIsConfirmOpen(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition duration-150"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmedDelete}
              className="px-4 py-2 bg-red-600 border border-transparent rounded-md text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition duration-150"
            >
              Yes, Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  // --- NEW: Notification Component ---
  const NotificationToast: React.FC = () => {
    if (!notification) return null;

    const baseClasses = "fixed bottom-5 right-5 p-4 rounded-lg shadow-xl flex items-center space-x-3 transition duration-300 ease-in-out z-50";
    const successClasses = "bg-green-100 text-green-800 border-l-4 border-green-500";
    const errorClasses = "bg-red-100 text-red-800 border-l-4 border-red-500";
    
    return (
      <div className={`${baseClasses} ${notification.type === 'success' ? successClasses : errorClasses}`}>
        {notification.type === 'success' ? (
          <CheckCircle className="w-6 h-6 text-green-500" />
        ) : (
          <XCircle className="w-6 h-6 text-red-500" />
        )}
        <p className="font-medium">{notification.message}</p>
        <button onClick={() => setNotification(null)} className="ml-4 text-gray-500 hover:text-gray-700">
          &times;
        </button>
      </div>
    );
  };


  return (
    <div className="min-h-screen bg-gray-100 p-6">

      <DeleteConfirmationModal />
      <NotificationToast />
      <div className="max-w-7xl mx-auto bg-white rounded-lg shadow-md p-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold text-gray-800">Product List</h2>
          <Link
            to="/create-product"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Plus className="h-5 w-5 mr-2" /> Add New Product
          </Link>
        </div>

        {/* Filters and Search */}
        <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 mb-6">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search products..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>

          <div className="flex-shrink-0">
            <label htmlFor="categoryFilter" className="sr-only">Filter by Category</label>
            <select
              id="categoryFilter"
              name="categoryFilter"
              className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              value={selectedCategory}
              onChange={handleCategoryChange}
            >
              {/* {uniqueCategories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))} */}
            </select>
          </div>

          <div className="flex-shrink-0">
            <label htmlFor="productTypeFilter" className="sr-only">Filter by Product Type</label>
            <select
              id="productTypeFilter"
              name="productTypeFilter"
              className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              value={productTypeFilter}
              onChange={(e) => setProductTypeFilter(e.target.value)}
            >
              <option value="All">All Types</option>
              <option value="gold">Gold</option>
              <option value="silver">Silver</option>
            </select>
          </div>
        </div>

        {/* Product Table */}
        <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Image
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Name
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Price
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Quantity
                </th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredProducts.map((product) => (
                <tr key={product.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <img
                          className="h-10 w-10 rounded-full object-cover"
                          src={product.image}
                          alt={product.name}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{product.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">₹{product.price.toFixed(2)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{product.stock}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100 transition duration-150 ease-in-out">
                        <Eye className="w-5 h-5" />
                      </button>
                      {/* TODO: Implement actual edit functionality and link to edit page */}
                      <button className="text-yellow-600 hover:text-yellow-800 p-2 rounded-full hover:bg-yellow-100 transition duration-150 ease-in-out">
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        className="text-red-600 hover:text-red-800 p-2 rounded-full hover:bg-red-100 transition duration-150 ease-in-out"
                       onClick={() => openDeleteConfirm(product)} // Assuming category helps determine type
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredProducts.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                    No products found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProductList;